"use client";

import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import CalculatorCard from "@/components/CalculatorCard";
import { CALCULATORS, DIY_CALCULATORS } from "@/lib/calculators-data";

const groups = [
  {
    id: "diy",
    title: "Material Estimators",
    description:
      "Model material volumes, coverage area, and package limits for clean hardware store runs.",
    countLabel: "10 Calculators",
    items: DIY_CALCULATORS,
  },
  {
    id: "interior",
    title: "Interior Cost Estimators",
    description:
      "Model renovation budgets for kitchen overhauls, master bathroom upgrades, and resale values.",
    countLabel: "6 Cost Models",
    items: CALCULATORS.filter((c) =>
      ["kitchen", "bathroom", "roi", "home-addition", "basement-finishing", "countertop"].includes(c.id),
    ),
  },
  {
    id: "exterior",
    title: "Exterior Cost Estimators",
    description:
      "Estimate contractor labor and material replacement factors for roofs, decks, and windows.",
    countLabel: "7 Cost Models",
    items: CALCULATORS.filter((c) =>
      ["roofing", "windows", "deck", "siding", "driveway", "pool", "gutters"].includes(c.id),
    ),
  },
  {
    id: "systems",
    title: "Systems & Energy Estimators",
    description:
      "Model long-term returns and system upgrades for HVAC units and solar panels.",
    countLabel: "3 Cost Models",
    items: CALCULATORS.filter((c) => ["solar", "hvac", "water-heater"].includes(c.id)),
  },
];

const filterTabs = [
  { id: "all", label: "All Tools" },
  { id: "diy", label: "Material Estimators" },
  { id: "interior", label: "Interior Cost Estimators" },
  { id: "exterior", label: "Exterior Cost Estimators" },
  { id: "systems", label: "Systems & Energy" },
];

export function CalculatorsHubGrid() {
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");

  useEffect(() => {
    const q = searchParams ? searchParams.get("q") : null;
    if (q) {
      setSearchQuery(q);
    }
  }, [searchParams]);

  const visibleGroups = useMemo(() => {
    if (selectedFilter === "all") return groups;
    return groups.filter((g) => g.id === selectedFilter);
  }, [selectedFilter]);

  const matchingCalculators = useMemo(() => {
    if (!searchQuery) return [];
    return CALCULATORS.filter((calc) => {
      const matchesSearch =
        calc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        calc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        calc.keywords.some((k) =>
          k.toLowerCase().includes(searchQuery.toLowerCase()),
        );

      let matchesFilter = true;
      if (selectedFilter === "diy") {
        matchesFilter = calc.category === "diy";
      } else if (selectedFilter === "interior") {
        matchesFilter = ["kitchen", "bathroom", "roi", "home-addition", "basement-finishing", "countertop"].includes(calc.id);
      } else if (selectedFilter === "exterior") {
        matchesFilter = ["roofing", "windows", "deck", "siding", "driveway", "pool", "gutters"].includes(calc.id);
      } else if (selectedFilter === "systems") {
        matchesFilter = ["solar", "hvac", "water-heater"].includes(calc.id);
      }

      return matchesSearch && matchesFilter;
    });
  }, [searchQuery, selectedFilter]);

  return (
    <div className="w-full flex flex-col gap-8">
      {/* Search & Filter Bar */}
      <div className="w-full flex flex-col md:flex-row gap-4 items-center justify-between border-b border-[var(--line)] pb-6">
        <div role="tablist" className="flex flex-wrap gap-2 items-center justify-start w-full md:w-auto">
          {filterTabs.map((tab) => {
            const isActive = selectedFilter === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                aria-controls={tab.id === "all" ? "diy interior exterior systems" : tab.id}
                id={`tab-hub-${tab.id}`}
                onClick={() => setSelectedFilter(tab.id)}
                className={`h-9 px-4 font-mono text-[10px] font-bold uppercase tracking-wider rounded transition-all border cursor-pointer ${
                  isActive
                    ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                    : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="relative w-full md:w-80 shrink-0">
          <input
            type="text"
            placeholder="Type to filter tools..."
            value={searchQuery}
            aria-label="Filter calculators"
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-3 pr-8 py-2 text-xs font-mono border rounded bg-[var(--surface-muted)] text-[var(--ink)]"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              aria-label="Clear search query"
              className="absolute inset-y-0 right-2 flex items-center text-[var(--ink-faint)] hover:text-[var(--ink)] cursor-pointer border-none bg-transparent"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Calculator Lists */}
      <div className="w-full flex flex-col gap-12">
        {searchQuery ? (
          <div className="flex flex-col items-start gap-6">
            <div className="w-full flex items-center gap-4">
              <h2 className="text-xl font-extrabold uppercase tracking-wider text-[var(--ink)] font-heading-condensed whitespace-nowrap text-left w-full">
                Search Results ({matchingCalculators.length})
              </h2>
            </div>
            {matchingCalculators.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
                {matchingCalculators.map((calc) => (
                  <div key={calc.id} className="relative">
                    <CalculatorCard calculator={calc} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="w-full py-12 text-center bg-[var(--surface-muted)] border border-dashed border-[var(--border)] rounded font-mono text-xs text-[var(--ink-soft)]">
                No calculators match your search parameters.
              </div>
            )}
          </div>
        ) : (
          visibleGroups.map((group) => (
            <section
              key={group.id}
              id={group.id}
              role="tabpanel"
              aria-labelledby={`tab-hub-${group.id}`}
              className="w-full flex flex-col items-start gap-6"
            >
              <div className="w-full flex flex-col gap-2">
                <div className="w-full flex items-center gap-4">
                  <h2 className="text-2xl font-extrabold uppercase tracking-wider text-[var(--ink)] font-heading-condensed whitespace-nowrap">
                    {group.title}
                  </h2>
                  <div className="flex-1 h-px bg-[var(--line-strong)]" />
                  <span className="text-[10px] font-bold text-[var(--ink-faint)] uppercase tracking-wider font-mono">
                    {group.countLabel}
                  </span>
                </div>
                <p className="text-xs leading-relaxed text-[var(--ink-soft)] max-w-3xl -mt-1 font-sans text-left">
                  {group.description}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
                {group.items.map((calc) => (
                  <div key={calc.id} className="relative">
                    <CalculatorCard calculator={calc} />
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
